import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { State } from "store/reducers";
import ls from "Localization";
import _ from "lodash";
import { Helmet } from "react-helmet-async";

import actions from "store/actions";

import { NFSeState } from "store/reducers/nfse";
import INFSe, { NFSeStatus } from "Types/INFSe";
import dayjs from "dayjs";
import Report, { ReportProps, ReportStateProps } from "Components/Report";
import { dateSort } from "Components/Report/helpers";
import { toStringCurrency } from "helpers/string";

import ImportModal from "./Import";

import { FaRegFilePdf, FaFileCode } from "react-icons/fa";
import RedoOutlinedIcon from "@mui/icons-material/RedoOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";

import Tooltip from "Components/Tooltip";
import DownloadNFSes from "./Download";
import DateSelectFilter from "Components/Report/DateSelectorFilter";
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Fab
} from "@mui/material";
import { MdAdd } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import useStyles from "./styles";
import Nfts from "./Nfts";
import { isInRole } from "helpers/auth";
import CancelDialog from "./CancelDialog";
import BulkCancelDialog from "./BulkCancelDialog";

let getNFSesDebounced: (() => any) & _.Cancelable = _.debounce(() => {}, 250);

function NFSes() {
	const [checked, setChecked] = useState<string[]>([]);
	const [messageModal, setMessageModal] = useState<React.ReactNode | null>(
		null
	);
	const [cancelDialog, setCancelDialog] = useState<{
		open: boolean;
		nfseId?: string;
		nfseCode?: string;
	}>({ open: false });
	const [bulkCancelDialog, setBulkCancelDialog] = useState<{
		open: boolean;
	}>({ open: false });

	const navigate = useNavigate();
	const dispatch = useDispatch<any>();

	// Enhanced callback to show modal for download results
	const enhancedCallback = (result: any) => {
		if (result && typeof result === 'object' && result.type) {
			// Show modal for download results
			setMessageModal(
				<Dialog
					open
					onClose={hideResendMessageModal}
					aria-labelledby="download-result-title"
					aria-describedby="download-result-description"
				>
					<DialogTitle id="download-result-title">
						{result.title}
					</DialogTitle>
					<DialogContent>
						<DialogContentText id="download-result-description">
							{result.message}
						</DialogContentText>
						{result.details && (
							<DialogContentText style={{ marginTop: '16px', fontSize: '0.875rem', color: '#666' }}>
								{result.details}
							</DialogContentText>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={hideResendMessageModal} color="primary">
							OK
						</Button>
					</DialogActions>
				</Dialog>
			);
		} else if (result && typeof result === 'function') {
			// If result is a function (original callback), call it
			result();
		}
	};
	
	const table = useRef<ReportProps>(null);

	const state = useSelector<State, NFSeState>(s => s.nfse);

	const { user } = useSelector<State, any>(s => s.login);

	useEffect(() => {
		dispatch(actions.nfse.clearValues());
	}, []);

	const hideResendMessageModal = () => setMessageModal(null);

	const handleRetry = (e, id: any) => {
		e.preventDefault();

		dispatch(
			actions.nfse.retry(id, err => {
				if (err) {
					console.log(err);
				} else {
					hideResendMessageModal();
					alert("Tentativa agendada, aguarde um momento e verifique se gerou");
				}
			})
		);
	};

	const handleRetries = e => {
		e.preventDefault();

		// Filter out "all" string that's added when select all is used
		const retryIds = checked.filter(id => id !== "all");

		dispatch(
			actions.nfse.retry(retryIds, err => {
				if (err) {
					console.log(err);
				} else {
					hideResendMessageModal();
					alert("Tentativa agendada, aguarde um momento e verifique se gerou");
				}
			})
		);
	};

	const handleResendMessage = (id?: string) =>
		setMessageModal(
			<Dialog
				open
				onClose={hideResendMessageModal}
				aria-labelledby="resend-message-dialog-title"
				aria-describedby="resend-message-description"
			>
				<DialogTitle id="resend-message-title">
					Reenviar not{id || checked.length === 1 ? "a" : "as"}?
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="resend-message-description">
						Tem certeza que deseja reenviar a
						{id || checked.length === 1 ? "" : "s"} not
						{id || checked.length === 1 ? "a" : "as"}?
					</DialogContentText>
				</DialogContent>
				<DialogActions style={{ padding: 20 }}>
					<Button
						onClick={e => (id ? handleRetry(e, id) : handleRetries(e))}
						color="primary"
					>
						Reenviar
					</Button>
					<Button onClick={hideResendMessageModal} color="secondary" autoFocus>
						Cancelar
					</Button>
				</DialogActions>
			</Dialog>
		);

	const handleCancelNFSe = (nfseId: string, nfseCode?: string) => {
		setCancelDialog({
			open: true,
			nfseId,
			nfseCode
		});
	};

	const handleConfirmCancel = (motivoCancelamento: number, justificativa: string) => {
		if (cancelDialog.nfseId) {
			dispatch(
				actions.nfse.cancel(
					cancelDialog.nfseId,
					motivoCancelamento,
					justificativa,
					(err: any) => {
						if (err) {
							console.log(err);
							alert("Erro ao cancelar NFSe: " + (err.error || "Erro desconhecido"));
						} else {
							alert("Cancelamento de NFSe agendado com sucesso!");
							setCancelDialog({ open: false });
							// Refresh the table
							load();
						}
					}
				)
			);
		}
	};

	const handleCloseCancelDialog = () => {
		setCancelDialog({ open: false });
	};

	const handleBulkCancelNFSe = () => {
		// Filter out "all" string and only NFSe with status 1 (Generated) for cancellation
		const cancellableIds = checked.filter(id => {
			// Skip the "all" string that's added when select all is used
			if (id === "all") return false;
			const nfse = state.items.find(item => item._id === id);
			return nfse && nfse.status === 1; // Only Generated status can be cancelled
		});

		if (cancellableIds.length === 0) {
			alert("Nenhuma NFSe selecionada pode ser cancelada. Apenas NFSe com status 'Gerada' podem ser canceladas.");
			return;
		}

		setBulkCancelDialog({ open: true });
	};

	const handleConfirmBulkCancel = (motivoCancelamento: number, justificativa: string) => {
		// Filter out "all" string and only NFSe with status 1 (Generated) for cancellation
		const cancellableIds = checked.filter(id => {
			// Skip the "all" string that's added when select all is used
			if (id === "all") return false;
			const nfse = state.items.find(item => item._id === id);
			return nfse && nfse.status === 1; // Only Generated status can be cancelled
		});

		if (cancellableIds.length > 0) {
			dispatch(
				actions.nfse.bulkCancel(
					cancellableIds,
					motivoCancelamento,
					justificativa,
					(err: any) => {
						if (err) {
							console.log(err);
							alert("Erro ao cancelar NFSe: " + (err.error || "Erro desconhecido"));
						} else {
							alert(`Cancelamento de ${cancellableIds.length} NFSe(s) agendado com sucesso!`);
							setBulkCancelDialog({ open: false });
							setChecked([]); // Clear selection
							// Refresh the table
							load();
						}
					}
				)
			);
		}
	};

	const handleCloseBulkCancelDialog = () => {
		setBulkCancelDialog({ open: false });
	};

	const handleDownloadSelected = () => {
		// Filter out "all" string that's added when select all is used
		const downloadIds = checked.filter(id => id !== "all");

		if (downloadIds.length === 0) {
			alert("Nenhuma NFSe selecionada para download.");
			return;
		}

		dispatch(
			actions.nfse.downloadSelectedNFSes(
				downloadIds,
				(result: any) => {
					if (result && typeof result === 'object' && result.type) {
						// Show modal for download results
						setMessageModal(
							<Dialog
								open
								onClose={hideResendMessageModal}
								aria-labelledby="download-result-title"
								aria-describedby="download-result-description"
							>
								<DialogTitle id="download-result-title">
									{result.title}
								</DialogTitle>
								<DialogContent>
									<DialogContentText id="download-result-description">
										{result.message}
									</DialogContentText>
									{result.details && (
										<DialogContentText style={{ marginTop: '16px', fontSize: '0.875rem', color: '#666' }}>
											{result.details}
										</DialogContentText>
									)}
								</DialogContent>
								<DialogActions>
									<Button onClick={hideResendMessageModal} color="primary">
										OK
									</Button>
								</DialogActions>
							</Dialog>
						);
					}
				}
			)
		);
	};

	const columns = useMemo<any[]>(() => {
		return [
			isInRole(user, ["admin"])
				? {
						Header: p => (
							<Checkbox
								id={"all"}
								checked={
									checked.filter(i => i === "all").length > 0 ? true : false
								}
								color="primary"
								style={{ padding: 4 }}
								inputProps={{ "aria-label": "primary checkbox" }}
								onChange={e => {
									if (
										checked.length === state.items.length ||
										checked.includes("all")
									) {
										setChecked([]);
									} else {
										const allChecked: string[] = [];
										state.items.forEach(p => {
											if (p._id) {
												allChecked.push(p._id);
											}
										});
										setChecked(allChecked.concat("all"));
									}
								}}
							/>
						),
						id: "checkbox",
						dontExport: true,
						show: true,
						accessor: p => (
							<Checkbox
								checked={
									checked.filter(i => i === p._id).length > 0 ? true : false
								}
								id={p._id}
								color="primary"
								style={{ padding: 4 }}
								inputProps={{ "aria-label": "secondary checkbox" }}
								onChange={e => {
									if (checked.length <= 0) {
										setChecked([...checked, e.target.id]);
									} else {
										//checa se o check está selecionado
										const filter = checked.filter(c => c === e.target.id);
										if (filter.length) {
											//retira o check
											const unCheck = checked.filter(c => c !== e.target.id);
											setChecked(unCheck);
										} else {
											setChecked([...checked, e.target.id]);
										}
									}
								}}
							/>
						),
						width: 45,
						sortable: false,
						filterable: false
				  }
				: null,
			{
				Header: ls.creationDate,
				id: "createdAt",
				accessor: (c: INFSe) =>
					dayjs(c.createdAt).format(ls.dateFormatWithoutSeconds),
				show: true,
				sortMethod: dateSort,
				width: 150,
				Filter: p => <DateSelectFilter {...p} />
			},
			{
				Header: "NFS-e",
				id: "code",
				show: true,
				accessor: (c: INFSe) => c.code
			},
			{
				Header: "Prestador",
				id: "mei.personName",
				show: true,
				accessor: (c: INFSe) =>
					c.mei?.businessName || c.mei?.personName || c.mei?.cnpj
			},
			{
				Header: "CNPJ Prestador",
				id: "mei.cnpj",
				show: true,
				accessor: (c: INFSe) => c.mei?.cnpj
			},
			isInRole(user, ["admin"])
				? {
						Header: "Empresa",
						id: "company",
						show: true,
						accessor: (c: INFSe) => c.company?.businessName || c.company?.cnpj,
						width: 250
				  }
				: null,
			isInRole(user, ["admin"])
				? {
						Header: "CNPJ Empresa",
						id: "cnpj",
						show: true,
						accessor: (c: INFSe) => c.company?.cnpj,
						width: 250
				  }
				: null,
			{
				Header: ls.value,
				id: "value",
				show: true,
				accessor: (c: INFSe) => toStringCurrency(+c.value / 100.0),
				width: 150
			},
			{
				Header: ls.pdf,
				id: "pdf",
				show: false,
				accessor: (c: INFSe) => c.pdf,
				width: 150
			},

			{
				Header: ls.xml,
				id: "xml",
				show: false,
				accessor: (c: INFSe) => c.xml,
				width: 150
			},

			{
				Header: "Chave Download",
				id: "dataKey",
				show: false,
				accessor: (c: INFSe) => c.dataKey,
				width: 150
			},
			{
				Header: ls.status,
				id: "status",
				show: true,
				accessor: (c: INFSe) => {
					if (c.status === NFSeStatus.Error) {
						return (
							<Tooltip title={c.statusReason}>
								{ls[NFSeStatus[c.status]]}
							</Tooltip>
						);
					}

					return ls2[NFSeStatus[c.status]];
				},
				width: 140,
				Filter: ({ filter, onChange }: any) => {
					return (
						<select
							onChange={event => onChange(event.target.value)}
							style={{ width: "100%" }}
							value={filter && filter.value ? filter.value : "all"}
						>
							<option value="">Todos</option>
							{Object.keys(NFSeStatus)
								.filter(c => !isNaN(+c))
								.sort((a, b) => +a - +b)
								.map((c: any) => (
									<option key={c} value={c}>
										{ls2[NFSeStatus[c]]}
									</option>
								))}
						</select>
					);
				}
			},
			{
				Header: "Criado por",
				id: "createdBy",
				show: true,
				accessor: (c: INFSe) => {
					if (c.createdBy) {
						return `${c.createdBy.name || ""} ${c.createdBy.lastname || ""}`.trim() || "N/A";
					}
					return "N/A";
				},
				width: 200
			},
			{
				Header: "Erro",
				id: "statusReason",
				show: true,
				filterable: false,
				accessor: (c: INFSe) => c.statusReason,
				width: 150
			},
			{
				Header: ls.actions,
				id: "actions",
				accessor: (c: any) => (
					<>
						{isInRole(user, ["admin"]) && c.status === 9 && (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flex: 1
								}}
							>
								<a
									href="!#"
									onClick={e => {
										e.preventDefault();
										handleResendMessage(c._id);
									}}
								>
									<Tooltip title="Tentar novamente">
										<RedoOutlinedIcon />
									</Tooltip>
								</a>
							</div>
						)}
						{isInRole(user, ["admin"]) && c.status === 1 && (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flex: 1
								}}
							>
								<a
									href="!#"
									onClick={e => {
										e.preventDefault();
										handleCancelNFSe(c._id, c.code);
									}}
								>
									<Tooltip title="Cancelar NFSe">
										<CancelIcon color="error" />
									</Tooltip>
								</a>
							</div>
						)}
						{c.pdf && (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flex: 1
								}}
							>
								<a href={c.pdf} target="_blank">
									<FaRegFilePdf />
								</a>
							</div>
						)}
						{c.xml && (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flex: 1
								}}
							>
								<a href={c.xml} target="_blank">
									<FaFileCode />
								</a>
							</div>
						)}
					</>
				),
				show: true,
				filterable: false,
				width: 80
			}
		].filter(c => c);
	}, [checked, user, state.items]);

	const handleFetchData = (tableState?: ReportStateProps) => {
		if (!tableState) return;
		const { page, pageSize, toExport, callback } = tableState;

		let { sorted, filtered } = tableState;

		if (!sorted) sorted = [];

		if (getNFSesDebounced) {
			getNFSesDebounced.cancel();
		}

		const creationDate = filtered.find((c: any) => c.id === "createdAt");

		if (creationDate?.value) {
			filtered = filtered.filter((c: any) => c.id !== "createdAt");

			if (creationDate?.value.startDate) {
				filtered.push({
					id: "startDate",
					value: creationDate.value.startDate
				});
			}

			if (creationDate?.value.endDate)
				filtered.push({
					id: "endDate",
					value: creationDate.value.endDate
				});
		}

		getNFSesDebounced = _.debounce(
			() =>
				dispatch(
					actions.nfse.getNFSes(
						page * pageSize,
						pageSize,
						filtered.reduce(
							(p: any, c: any) => p + `&filters[${c.id}]=${c.value}`,
							""
						),
						sorted[0]?.id,
						sorted[0]?.desc,
						toExport,
						callback
					)
				),
			500
		);

		getNFSesDebounced();
	};

	const load = () => handleFetchData(table.current?.state);

	const ls2: any = ls;

	const classes = useStyles({});

	return (
		<div>
			<Helmet>
				<title>
					{ls.nfses} | {ls.appName}
				</title>
			</Helmet>
			<Report
				manual
				tableRef={table}
				headerRightComponent={
					<>
						<DownloadNFSes tableState={table.current?.state} enhancedCallback={enhancedCallback} />
						{isInRole(user, ["admin"]) && <ImportModal load={load} />}
						{isInRole(user, ["admin"]) && <Nfts />}
					</>
				}
				defaultFilterMethod={(filter, row) =>
					String(row[filter.id])
						.toLowerCase()
						.indexOf(filter.value.toLowerCase()) > -1
				}
				title={ls.nfses}
				data={state.items}
				pages={state.pages}
				loading={state.loading.getAll || state.loading.import}
				columns={columns}
				onFetchData={handleFetchData}
				filterable
				visibleColumns={columns
					.filter(c => c.show)
					.map(c => c.Header.toString())}
				defaultSorted={[
					{
						id: "createdAt",
						desc: true
					}
				]}
				getTdProps={() => {
					return {
						style: {
							display: "flex",
							alignItems: "center"
						}
					};
				}}
				// onRowClicked={data => navigate(`/Dashboard/NFSe/${data._id}`)}
			/>
			<Fab
				color="primary"
				aria-label="Add"
				style={{
					position: "fixed",
					bottom: "16px",
					right: "16px",
					zIndex: 1000
				}}
				onClick={() => navigate("/Dashboard/NFSe/Create")}
			>
				<MdAdd size={24} />
			</Fab>
			{messageModal}
			<CancelDialog
				open={cancelDialog.open}
				onClose={handleCloseCancelDialog}
				onConfirm={handleConfirmCancel}
				nfseCode={cancelDialog.nfseCode}
				loading={state.loading.cancel}
			/>
			<BulkCancelDialog
				open={bulkCancelDialog.open}
				onClose={handleCloseBulkCancelDialog}
				onConfirm={handleConfirmBulkCancel}
				selectedCount={checked.filter(id => {
					// Skip the "all" string that's added when select all is used
					if (id === "all") return false;
					const nfse = state.items.find(item => item._id === id);
					return nfse && nfse.status === 1; // Only count Generated status
				}).length}
				loading={state.loading.cancel}
			/>
			{checked.length >= 1 && (
				<Tooltip title="Reenviar">
					<Fab
						color="primary"
						aria-label="Send"
						style={{
							position: "fixed",
							bottom: "94px",
							right: "16px",
							zIndex: 1000
						}}
						onClick={() => handleResendMessage()}
					>
						<RedoOutlinedIcon />
					</Fab>
				</Tooltip>
			)}
			{checked.length >= 1 && isInRole(user, ["admin"]) && (
				<Tooltip title="Cancelar NFSe">
					<Fab
						color="error"
						aria-label="Cancel"
						style={{
							position: "fixed",
							bottom: "172px",
							right: "16px",
							zIndex: 1000
						}}
						onClick={handleBulkCancelNFSe}
					>
						<CancelIcon />
					</Fab>
				</Tooltip>
			)}
			{checked.length >= 1 && (
				<Tooltip title="Baixar NFSe Selecionadas">
					<Fab
						color="secondary"
						aria-label="Download Selected"
						style={{
							position: "fixed",
							bottom: "250px",
							right: "16px",
							zIndex: 1000
						}}
						onClick={handleDownloadSelected}
					>
						<DownloadIcon />
					</Fab>
				</Tooltip>
			)}
		</div>
	);
}

export default NFSes;
